import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AllapiService } from '../allapi.service';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../header/header.component';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CommentSectionComponent } from '../comment-section/comment-section.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { normalizeStoryText } from '../utils/text-normalizer';
import { AuthService } from '../auth.service';
import { LanguageService } from '../language.service';
import { TranslationService } from '../translation.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-series',
  standalone: true,
  imports: [HeaderComponent, CommonModule, CommentSectionComponent, FormsModule],
  templateUrl: './series.component.html',
  styleUrl: './series.component.css'
})
export class SeriesComponent implements OnInit {
  storyId: number | null = null;
  storyContent: any = null;
  storyDetail: any = {}; 
  currentUser: any = null; 
  allParts: any[] = [];
  isLoading = true;
  errorMessage = '';
  scrollProgress = '0%';
  partsLoaded = false;
  isSpeaking = false;
  isFullscreen = false;
  userId: number | null = null;
  isUserLoggedIn = false; // NEW: Track login status
  selectedLanguage: string = 'en';
  hideAuthorImage = false;
  storyImgPath = 'https://stories.funtelstudio.com/storyapi/';
  private followStatusChecked = true;
  
  followerCount: number = 0;
  followerUsers: any[] = [];

  fontSize = 1;
  readerMode: 'light' | 'dark' | 'reading' = 'light';
  isFollowing: boolean = false;
  userRating: number = 0;
  hoverRating: number = 0;
  averageRating: number = 0;
  totalRatings: number = 0;
  videoUrl: string = 'https://funtel.b-cdn.net/';

  @ViewChild('storyContentBox') storyContentDiv!: ElementRef;
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;
  isPlaying = false;
  progressBarWidth = '0%';
  currentTimeDisplay = '0:00';
  durationDisplay = '0:00';
  audioLoaded = false;
  paymentProcessing: boolean = false;
  userReaderBalance: number = 0;

  // Related stories properties
  relatedStories: any[] = [];
  isInLibrary = false;
  showBalanceWarning = false;
  nextEpisodePrice = 0;
  private controlsTimeout: any;

  // Payment popup properties
  showPaymentPopup = false;
  paymentPopupData: any = {
    episodePrice: 0,
    currentBalance: 0,
    shortfall: 0,
    nextPart: null
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contentService: AllapiService,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    public translationService: TranslationService,
    private languageService: LanguageService,
    private authService: AuthService
  ) {}

  ngAfterViewInit() {
    if (this.audioPlayer) {
      this.audioPlayer.nativeElement.addEventListener('loadedmetadata', () => {
        this.audioLoaded = true;
        this.updateDurationDisplay();
      });
    }
  }

  ngOnInit(): void {
    this.selectedLanguage = this.languageService.getCurrentLanguage();
    this.languageService.currentLanguage$.subscribe(lang => {
      this.selectedLanguage = lang;
      this.loadRelatedStories();
    });
    
    window.scrollTo(0, 0);
    
    // Check login status
    this.authService.currentUser$.subscribe(user => {
      if (user?.id) {
        this.userId = +user.id;
        this.isUserLoggedIn = true;
        console.log('✅ User logged in:', this.userId);
      } else {
        console.log('⚠️ User not logged in');
        this.isUserLoggedIn = false;
        this.userId = null;
      }
    });

    this.route.paramMap.subscribe(params => {
      const param = params.get('idAndTitle');
      if (param) {
        const id = Number(param.split('-')[0]);
        this.storyId = id;
        console.log('📖 Story ID loaded:', this.storyId);
        this.loadStory();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
    
    // Load wallet balance only if user is logged in
    this.authService.currentUser$.subscribe(user => {
  if (user?.id) {
    this.userId = +user.id;
    this.isUserLoggedIn = true;

    console.log('✅ User logged in:', this.userId);

    // 🔥 LOAD BALANCE HERE
    this.loadUserWalletBalance();
  } else {
    this.isUserLoggedIn = false;
    this.userId = null;
    this.userReaderBalance = 0;
  }
});
    
    this.loadRatings();
    this.checkIfInLibrary();
  }

  // ========== WALLET BALANCE METHODS ==========
  loadUserWalletBalance(): Promise<number> {
    return new Promise((resolve) => {
      if (!this.isUserLoggedIn) {
        console.log('⚠️ User not logged in, wallet balance not available');
        this.userReaderBalance = 0;
        resolve(0);
        return;
      }

      const userId = this.authService.getCurrentUserId();
      if (!userId) return resolve(0);

      this.http.get<any>("https://stories.funtelstudio.com/storyapi/get_wallet_data.php?type=reader",
        { withCredentials: true }
      ).subscribe({
        next: (response) => {
          this.userReaderBalance = response?.reader_wallet?.balance || 0;
          console.log("sddsdsa", this.userReaderBalance)
          resolve(this.userReaderBalance);
        },
        error: () => {
          this.userReaderBalance = 0;
          resolve(0);
        }
      });
    });
  }

  // ========== AUDIO PLAYER METHODS ==========
  toggleAudioPlayback() {
    if (!this.storyContent?.audio) return;

    if (this.isPlaying) {
      this.audioPlayer.nativeElement.pause();
    } else {
      this.audioPlayer.nativeElement.play().catch(error => {
        console.error('Audio playback failed:', error);
      });
    }
    this.isPlaying = !this.isPlaying;
  }

  updateProgressBar() {
    if (!this.audioPlayer) return;
    
    const audio = this.audioPlayer.nativeElement;
    const progress = (audio.currentTime / audio.duration) * 100;
    this.progressBarWidth = `${progress}%`;
    this.updateCurrentTimeDisplay();
  }

  seekAudio(event: MouseEvent) {
    if (!this.audioPlayer || !this.audioLoaded) return;
    
    const progressBar = event.currentTarget as HTMLElement;
    const rect = progressBar.getBoundingClientRect();
    const seekPosition = (event.clientX - rect.left) / rect.width;
    
    this.audioPlayer.nativeElement.currentTime = seekPosition * this.audioPlayer.nativeElement.duration;
  }

  updateCurrentTimeDisplay() {
    const currentTime = Math.floor(this.audioPlayer.nativeElement.currentTime);
    const minutes = Math.floor(currentTime / 60);
    const seconds = currentTime % 60;
    this.currentTimeDisplay = `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  }

  updateDurationDisplay() {
    const duration = Math.floor(this.audioPlayer.nativeElement.duration);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    this.durationDisplay = `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  }

  onAudioEnded() {
    this.isPlaying = false;
    this.progressBarWidth = '0%';
    this.currentTimeDisplay = '0:00';
  }

  // ========== STORY NAVIGATION METHODS ==========
  async continueToNextPart() {
    // Check if user is logged in
    if (!this.isUserLoggedIn) {
      console.log('🔒 Login required to continue reading');
      this.navigateToLogin();
      return;
    }

    const currentIndex = this.allParts.findIndex(p => p.id == this.storyId);
    if (currentIndex === -1) return;

    const nextPart = this.allParts[currentIndex + 1];
    const episodePrice = nextPart.monetization_amount || 0;

    // Wait until wallet actually loads
    const balance = await this.loadUserWalletBalance();

    console.log("👛 Updated Balance:", balance);

    if (nextPart.monetization_enabled && episodePrice > 0) {
      if (balance >= episodePrice) {
        this.processNextEpisodePayment(nextPart);
      } else {
        this.showInsufficientBalancePopup(nextPart, episodePrice);
      }
    } else {
      this.proceedToNextPart(nextPart);
    }
  }

  // ========== LOGIN NAVIGATION ==========
  navigateToLogin(): void {
    this.router.navigate(['/login'], {
      queryParams: {
        returnUrl: this.router.url,
        message: 'Login required to continue reading'
      }
    });
  }

  showInsufficientBalancePopup(nextPart: any, episodePrice: number): void {
    const currentBalance = this.userReaderBalance;
    const shortfall = episodePrice - currentBalance;
    
    this.paymentPopupData = {
      episodePrice: episodePrice,
      currentBalance: currentBalance,
      shortfall: shortfall > 0 ? shortfall : 0,
      nextPart: nextPart
    };
    
    this.showPaymentPopup = true;
    console.log('💰 Insufficient balance popup shown:', this.paymentPopupData);
  }

  closePaymentPopup(): void {
    this.showPaymentPopup = false;
    this.paymentPopupData = {
      episodePrice: 0,
      currentBalance: 0,
      shortfall: 0,
      nextPart: null
    };
  }

  navigateToWallet(): void {
    this.closePaymentPopup();
    this.router.navigate(['/wallet']);
  }

  processNextEpisodePayment(nextPart: any): void {
    console.log('🔧 processNextEpisodePayment() STARTED', nextPart);
    
    if (this.paymentProcessing) {
      console.log('❌ Payment already processing, returning...');
      return;
    }

    const episodePrice = nextPart.monetization_amount || 0;
    console.log('💰 Episode Price:', episodePrice);
    
    // Use the correct content ID - episode ID
    const contentId = nextPart.id;
    console.log('🎯 Content ID to use:', contentId);
    
    if (!contentId || contentId === 0) {
      console.error('❌ INVALID CONTENT ID:', contentId);
      alert('Error: Invalid content ID');
      return;
    }

    console.log('🔄 Checking purchase status...');
    this.checkEpisodePurchaseStatus(contentId).then(hasPurchased => {
      console.log('✅ Purchase status result:', hasPurchased);
      
      if (hasPurchased) {
        console.log('✅ Already purchased, proceeding to next part');
        this.proceedToNextPart(nextPart);
      } else {
        console.log('🆕 Not purchased, processing payment...');
        this.processPayment(nextPart, episodePrice, contentId);
      }
    }).catch(error => {
      console.error('❌ Error checking purchase status:', error);
      this.processPayment(nextPart, episodePrice, contentId);
    });
  }

  processPayment(nextPart: any, episodePrice: number, contentId: number): void {
    console.log('🚀 processPayment STARTED');
    this.paymentProcessing = true;

    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      console.log('❌ No user ID');
      this.router.navigate(['/login']);
      return;
    }

    const paymentData = {
      user_id: userId,
      content_id: contentId,
      amount: episodePrice
    };

    console.log('📤 Sending payment request:', paymentData);

    this.http.post<any>('https://stories.funtelstudio.com/storyapi/deduct_episode_payment.php', 
      paymentData, 
      { withCredentials: true }
    ).subscribe({
      next: (response) => {
        console.log('✅ Payment API Response:', response);
        this.paymentProcessing = false;
        
        if (response.status === 'success') {
          // Update local balance
          this.userReaderBalance = response.new_balance || 0;
          console.log('💰 Balance updated to:', this.userReaderBalance);
          
          // Show success message
          this.showPaymentSuccess(episodePrice, this.userReaderBalance);
          
          // Proceed to next part
          this.proceedToNextPart(nextPart);
        } else {
          console.error('❌ Payment failed in response:', response);
          alert('Payment failed: ' + (response.message || 'Unknown error'));
        }
      },
      error: (error) => {
        console.error('❌ Payment API Error:', error);
        this.paymentProcessing = false;
        
        let errorMessage = 'Payment processing failed';
        if (error.error && error.error.message) {
          errorMessage += ': ' + error.error.message;
        } else if (error.message) {
          errorMessage += ': ' + error.message;
        }
        
        alert(errorMessage);
      }
    });
  }

  proceedToNextPart(nextPart: any): void {
    console.log('➡️ Proceeding to next part:', nextPart.id);
    
    // Store read progress
    if (this.storyDetail?.id) {
      this.storeReadProgress(
        this.storyDetail.id,
        nextPart.id,
        this.allParts.length,
        nextPart.story_title
      );
    }

    // Update current story ID
    this.storyId = nextPart.id;
    this.isLoading = true;
    this.storyContent = null;

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Load the next part
    this.loadStory();

    // Update URL
    const slug = this.slugify(nextPart.story_title || 'story');
    this.router.navigate(['/series', `${nextPart.id}-${slug}`], { 
      replaceUrl: true 
    }).then(() => {
      console.log('✅ Navigation complete to part:', nextPart.id);
    });
  }

  // ========== PURCHASE CHECK METHOD ==========
  checkEpisodePurchaseStatus(contentId: number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const userId = this.authService.getCurrentUserId();
      if (!userId) {
        resolve(false);
        return;
      }

      this.http.post<any>('https://stories.funtelstudio.com/storyapi/check_episode_purchase.php', {
        user_id: userId,
        content_id: contentId
      }, {
        withCredentials: true
      }).subscribe({
        next: (response) => {
          console.log('✅ Purchase check response:', response);
          resolve(response.has_purchased || false);
        },
        error: (error) => {
          console.error('❌ Purchase check error:', error);
          resolve(false);
        }
      });
    });
  }

  showPaymentSuccess(amount: number, newBalance: number): void {
    console.log(`✅ Payment successful! ₹${amount} deducted. New balance: ₹${newBalance}`);
  }

  // ========== RATING METHODS ==========
  loadRatings() {
    if (!this.storyId) return;
    const headers = new HttpHeaders({
        'content-type': 'application/json',
    });
    this.http.post<any>(
        'https://stories.funtelstudio.com/storyapi/get_rating.php',
        { story_id: this.storyId },
        { headers, withCredentials: true }
    ).subscribe({
        next: (res) => {
            console.log('Rating response:', res); // Debug log
            this.userRating = res.user_rating || 0;
            this.averageRating = res.average_rating || 0;
            this.totalRatings = res.total_ratings || 0;
            console.log('Ratings loaded - User Rating:', this.userRating); // Debug
        },
        error: (err) => {
            console.error('Rating load failed', err);
        }
    });
  }

  submitRating(star: number) {
    console.log('Submitting rating:', star); // Debug
    
    // Check if user is logged in
    if (!this.isUserLoggedIn) {
      this.navigateToLogin();
      return;
    }
    
    const userId = this.authService.getCurrentUserId();
    if (!userId) { 
        this.navigateToLogin();
        return; 
    }
    
    // If clicking same star again, remove rating
    const ratingToSubmit = (this.userRating === star) ? 0 : star;
    
    const headers = new HttpHeaders({
        'content-type': 'application/json',
    });
    this.http.post<any>(
        'https://stories.funtelstudio.com/storyapi/save_rating.php',
        { story_id: this.storyId, rating: ratingToSubmit },
        { headers, withCredentials: true }
    ).subscribe({
        next: (res) => {
            console.log('Rating save response:', res); // Debug
            this.userRating = ratingToSubmit;
            this.loadRatings(); // Reload to get updated average
        },
        error: (err) => {
            console.error('Rating submit failed', err);
        }
    });
  }

  onStarHover(starNumber: number): void {
    this.hoverRating = starNumber;
  }

  onStarLeave(): void {
    this.hoverRating = 0;
  }

  // ========== FULLSCREEN READER METHODS ==========
  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    
    if (this.isFullscreen) {
      document.body.style.overflow = 'hidden';
      
      // Load balance before entering fullscreen
      if (this.isUserLoggedIn) {
        this.loadUserWalletBalance();
      }
      
      setTimeout(() => {
        const reader = document.getElementById('fullscreenReader');
        if (reader) {
          reader.scrollTop = 0;
        }
      }, 100);
      
      setTimeout(() => {
        const controls = document.getElementById('readerControls');
        if (controls && this.isFullscreen) {
          controls.style.opacity = '0.3';
          controls.style.transition = 'opacity 0.3s ease';
        }
      }, 5000);
    } else {
      document.body.style.overflow = '';
      
      const controls = document.getElementById('readerControls');
      if (controls) {
        controls.style.opacity = '1';
      }
    }
  }

  changeReaderMode(mode: 'light' | 'dark' | 'reading'): void {
    this.readerMode = mode;
  }

  adjustFontSize(action: 'increase' | 'decrease'): void {
    if (action === 'increase' && this.fontSize < 1.5) {
      this.fontSize += 0.1;
    } else if (action === 'decrease' && this.fontSize > 0.8) {
      this.fontSize -= 0.1;
    }
  }

  // ========== FOLLOW/UNFOLLOW METHODS ==========
  getFollowingUsers() {
    if (!this.storyDetail?.author_id) return;

    const headers = new HttpHeaders({
      'content-type': 'application/json',
    });
    this.http.post<any>(
      'https://stories.funtelstudio.com/storyapi/follow/check_follow.php',
      { following_id: this.storyDetail.author_id },
      { headers: headers, withCredentials: true }
    ).subscribe({
      next: (res) => {
        this.followerUsers = res.followersList || [];
        this.followerCount = res.followerCount || 0;
      },
      error: (err) => {
        console.error('Failed to load followers', err);
      }
    });
  }

  checkFollowStatus(): void {
  const authorId = this.storyDetail?.author_id;

  if (!this.userId || !authorId || this.userId === authorId) {
    this.isFollowing = false;
    return;
  }

  const headers = new HttpHeaders({
    'content-type': 'application/json',
  });

  this.http.post<any>(
    'https://stories.funtelstudio.com/storyapi/follow/check_follow.php',
    {
      follower_id: this.userId,
      following_id: authorId
    },
    { headers, withCredentials: true }
  ).subscribe({
    next: (res) => {
      this.isFollowing = !!res.isFollowing;
    }
  });
}

get canShowFollowButton(): boolean {
  if (!this.isUserLoggedIn) return false;
  if (!this.userId) return false;
  if (!this.storyDetail?.author_id) return false;

  return Number(this.storyDetail.author_id) !== Number(this.userId);
}
toggleFollow(): void {
  const authorId = this.storyDetail?.author_id;
  const userId = this.authService.getCurrentUserId();

  // 🔒 Not logged in
  if (!userId) {
    this.navigateToLogin();
    return;
  }

  // 🔒 SELF FOLLOW BLOCK
  if (userId === authorId) {
    console.warn('❌ Self follow not allowed');
    return;
  }

  const apiUrl = this.isFollowing
    ? 'https://stories.funtelstudio.com/storyapi/follow/unfollow.php'
    : 'https://stories.funtelstudio.com/storyapi/follow/follow.php';

  const headers = new HttpHeaders({
    'content-type': 'application/json',
  });

  const body = {
    follower_id: userId,
    following_id: authorId
  };

  this.http.post<any>(apiUrl, body, { headers, withCredentials: true })
    .subscribe({
      next: () => {
        this.isFollowing = !this.isFollowing;
        this.getFollowingUsers(); // refresh count
      },
      error: (err) => {
        console.error('❌ Follow/Unfollow failed:', err);
      }
    });
}

  // ========== STORY LOADING METHODS ==========
  loadStory(): void {
    if (!this.storyId) return;

    this.contentService.getStoryById(this.storyId).subscribe({
      next: (response: any) => {
        if (response.status === 'success' && response.data) {
          let content = response.data.content;
          content = normalizeStoryText(content);

          this.storyContent = {
            title: response.data.story_title,
            description: response.data.description,
            content: response.data.content,
            date: response.data.date || response.data.created_at || '',
            user_id: response.data.user_id,
            video_url: response.data.video_link 
              ? response.data.video_link.replace(/^\/+/, '') 
              : '',
            audio: response.data.audio,
            monetization_enabled: response.data.monetization_enabled,
            monetization_amount: response.data.monetization_amount
          };

          const storiesId = response.data.stories_id;

          if (storiesId) {
            this.loadAllParts(storiesId);
            this.fetchStoryDetail(storiesId);
          }

          this.checkAndProcessEpisodePayment(response.data);
          this.loadRatings();
          
          // Load related stories
          setTimeout(() => {
            this.loadRelatedStories();
          }, 1000);
          
        } else {
          this.errorMessage = 'Story not found';
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('❌ Error loading story:', err);
        this.errorMessage = 'Error loading story.';
        this.isLoading = false;
      }
    });
  }

  fetchStoryDetail(storiesId: number) {
    const headers = new HttpHeaders({
      'content-type': 'application/json',
    });

    const userId = this.authService.getCurrentUserId();
    if (userId) {
      this.userId = +userId;
    }

    this.http.post<{ status: string; data: any }>(
      `https://stories.funtelstudio.com/storyapi/getstorydetails.php?id=${storiesId}`,
      {},
      { headers, withCredentials: true }
    ).subscribe({
      next: (response) => {
        if (response.status === 'success' && response.data) {
          this.storyDetail = response.data;
          this.storyDetail.author_id = this.storyDetail.user_id;

          this.checkFollowStatus();
          this.getFollowingUsers();
          
          // Load related stories
          setTimeout(() => {
            this.loadRelatedStories();
          }, 500);
          
        } else {
          this.errorMessage = 'Story detail not found.';
        }
      },
      error: (error) => {
        console.error('Error fetching story detail:', error);
        this.errorMessage = 'Failed to load story detail.';
      }
    });
  }

  loadAllParts(storiesId: number): void {
    const apiUrl = `https://stories.funtelstudio.com/storyapi/get_user_ep.php?stories_id=${storiesId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    this.http.post<{ status: string, data: any[] }>(
      apiUrl,
      { user_id: sessionStorage.getItem('userId') },
      { headers }
    ).subscribe({
      next: (res) => {
        if (res.status === 'success' && res.data) {
          this.allParts = res.data.filter(p => p.status == 1).sort((a, b) => a.id - b.id);
          this.partsLoaded = true;

          const currentIndex = this.allParts.findIndex(p => p.id == this.storyId);
          const currentPart = this.allParts[currentIndex];

          if (currentPart) {
            const isLastPart = currentIndex === this.allParts.length - 1;
            this.storeReadProgress(
              storiesId,
              currentPart.id,
              this.allParts.length,
              currentPart.story_title
            );
          }
        }
      },
      error: (err) => {
        console.error('❌ Error loading parts:', err);
        this.allParts = [];
        this.partsLoaded = true;
      }
    });
  }

  // ========== RELATED STORIES METHODS (DYNAMIC) ==========
  loadRelatedStories(): void {
    if (!this.storyDetail) {
      console.log('❌ Cannot load related stories: No story detail');
      this.loadFallbackRelatedStories();
      return;
    }

    // Get category ID or genre for related stories
    const categoryId = this.storyDetail.category_id;
    const genre = this.storyDetail.genre;
    
    if (!categoryId && !genre) {
      console.log('❌ Cannot load related stories: No category or genre');
      this.loadFallbackRelatedStories();
      return;
    }

    console.log('🔍 Loading related stories for:', { categoryId, genre });
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    // Try category first, then genre
    let apiUrl = '';
    if (categoryId) {
      apiUrl = `https://stories.funtelstudio.com/storyapi/allstory.php?category_id=${categoryId}&limit=10`;
    } else if (genre) {
      apiUrl = `https://stories.funtelstudio.com/storyapi/allstory.php?genre=${encodeURIComponent(genre)}&limit=10`;
    }

    console.log('📡 Calling API for related stories:', apiUrl);

    this.http.get<any>(apiUrl, {
      headers: headers,
      withCredentials: true
    }).subscribe({
      next: (res) => {
        console.log('✅ Related Stories API Response:', res);
        
        if (res.status === 'success' && res.data && res.data.length > 0) {
          // Filter out current story and stories without title
          const filteredStories = res.data.filter((story: any) => 
            story.id !== this.storyId && 
            story.story_title
          );
          
          if (filteredStories.length > 0) {
            // Shuffle and pick random 3 stories
            const shuffled = [...filteredStories].sort(() => 0.5 - Math.random());
            const randomStories = shuffled.slice(0, 3);
            
            this.relatedStories = randomStories.map((story: any) => ({
              id: story.id,
              title: story.story_title || 'Untitled Story',
              author: story.author_name || 'Unknown Author',
              rating: parseFloat(story.average_rating) || 0,
              views: story.views || 0,
              category_id: story.category_id,
              story_image: story.story_image ? `${this.storyImgPath}${story.story_image}` : null,
              genre: story.genre || 'General',
              description: story.description || ''
            }));
            
            console.log('🎯 Dynamic related stories loaded:', this.relatedStories.length);
          } else {
            this.loadFallbackRelatedStories();
          }
        } else {
          this.loadFallbackRelatedStories();
        }
      },
      error: (err) => {
        console.error('❌ Error loading related stories:', err);
        this.loadFallbackRelatedStories();
      }
    });
  }

  private loadFallbackRelatedStories(): void {
    console.log('🔄 Loading fallback related stories');
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    
    // Get trending/popular stories as fallback
    const apiUrl = `https://stories.funtelstudio.com/storyapi/allstory.php?limit=10`;
    
    this.http.get<any>(apiUrl, {
      headers: headers,
      withCredentials: true
    }).subscribe({
      next: (res) => {
        if (res.status === 'success' && res.data && res.data.length > 0) {
          const filteredStories = res.data.filter((story: any) => story.id !== this.storyId);
          
          if (filteredStories.length > 0) {
            // Shuffle for randomness
            const shuffled = [...filteredStories].sort(() => 0.5 - Math.random());
            const randomStories = shuffled.slice(0, 3);
            
            this.relatedStories = randomStories.map((story: any) => ({
              id: story.id,
              title: story.story_title || 'Popular Story',
              author: story.author_name || 'Community Picks',
              rating: parseFloat(story.average_rating) || 4.3,
              views: story.views || 1500,
              story_image: story.story_image ? `${this.storyImgPath}${story.story_image}` : null,
              genre: story.genre || 'General',
              description: story.description || ''
            }));
            console.log('📚 Fallback stories loaded from API');
          } else {
            this.loadDefaultRelatedStories();
          }
        } else {
          this.loadDefaultRelatedStories();
        }
      },
      error: () => {
        this.loadDefaultRelatedStories();
      }
    });
  }

  private loadDefaultRelatedStories(): void {
    console.log('📚 Loading default related stories');
    
    this.relatedStories = [
      {
        id: 101,
        title: 'Popular Stories',
        author: 'Community Picks',
        rating: 4.3,
        views: 1500,
        story_image: null,
        genre: 'General',
        description: 'Top trending stories from the community'
      },
      {
        id: 102,
        title: 'Trending Now',
        author: 'Editor\'s Choice',
        rating: 4.5,
        views: 2200,
        story_image: null,
        genre: 'General',
        description: 'Editor selected trending stories'
      },
      {
        id: 103,
        title: 'You Might Like',
        author: 'Based on Your Reading',
        rating: 4.2,
        views: 1800,
        story_image: null,
        genre: 'General',
        description: 'Recommended based on your reading history'
      }
    ];
  }

  navigateToRelatedStory(relatedStory: any): void {
    if (!relatedStory.id) return;
    
    console.log('📍 Navigating to related story:', relatedStory.id, relatedStory.title);
    
    const slug = this.slugify(relatedStory.title || 'story');
    this.router.navigate(['/story', `${relatedStory.id}-${slug}`]);
  }

  // ========== EPISODE PAYMENT CHECK ==========
  checkAndProcessEpisodePayment(storyData: any): void {
    if (!storyData.monetization_enabled || !storyData.monetization_amount || storyData.monetization_amount <= 0) {
      console.log('🆓 Free episode, no payment required');
      return;
    }

    // Check if user is logged in
    if (!this.isUserLoggedIn) {
      console.log('🔒 User not logged in, cannot check payment');
      return;
    }

    // Check if user is the author
    if (this.userId && this.userId === storyData.user_id) {
      console.log('👑 Author reading own episode - free access');
      return;
    }

    const episodePrice = storyData.monetization_amount;
    const episodeId = storyData.id;
    const storyId = storyData.stories_id;

    console.log('💰 Checking payment for episode:', {
      episodeId: episodeId,
      storyId: storyId,
      price: episodePrice,
      userBalance: this.userReaderBalance
    });

    this.processEpisodePayment(episodeId, storyId, episodePrice);
  }

  processEpisodePayment(episodeId: number, storyId: number, amount: number): void {
    console.log('💳 Processing payment for episode:', episodeId);
    
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      console.log('❌ User not logged in');
      return;
    }

    // Check if user is author
    if (this.userId && this.userId === this.storyDetail?.author_id) {
      console.log('👑 Author reading own episode - free access');
      return;
    }

    const paymentData = {
      user_id: userId,
      content_id: episodeId,
      amount: amount
    };

    console.log('📤 Calling payment API:', paymentData);

    this.http.post<any>('https://stories.funtelstudio.com/storyapi/deduct_episode_payment.php', 
      paymentData, 
      { withCredentials: true }
    ).subscribe({
      next: (response) => {
        console.log('✅ PAYMENT API RESPONSE:', response);
        
        if (response.status === 'success') {
          this.userReaderBalance = response.new_balance;
          console.log('💰 New balance:', this.userReaderBalance);
          this.showPaymentSuccess(amount, this.userReaderBalance);
        } else {
          console.error('❌ Payment failed:', response.message);
          if (response.message && response.message.includes('already purchased')) {
            console.log('🔄 Episode already purchased, continuing...');
          }
        }
      },
      error: (error) => {
        console.error('❌ Payment API error:', error);
      }
    });
  }

  // ========== UI HELPER METHODS ==========
  isLastPart(): boolean {
    if (!this.partsLoaded || !this.storyId || !this.allParts.length) return true;
    const currentIndex = this.allParts.findIndex(part => part.id == this.storyId);
    return currentIndex === -1 || currentIndex === this.allParts.length - 1;
  }

  isFirstPart(): boolean {
    if (!this.partsLoaded || !this.storyId || !this.allParts.length) return true;
    return this.allParts[0].id == this.storyId;
  }

  getCurrentPartNumber(): number {
    if (!this.storyId || !this.allParts.length) return 0;
    const index = this.allParts.findIndex(part => part.id == this.storyId);
    return index + 1;
  }

  getNextEpisodePrice(): number {
    const currentIndex = this.allParts.findIndex(part => part.id == this.storyId);
    if (currentIndex !== -1 && currentIndex < this.allParts.length - 1) {
      const nextPart = this.allParts[currentIndex + 1];
      
      // Check if user is the author
      if (this.userId && this.storyDetail?.author_id && this.userId === this.storyDetail.author_id) {
        return 0; // Free for author
      }
      
      // Check if episode is free
      if (!nextPart.monetization_enabled || nextPart.monetization_amount === 0) {
        return 0; // Free episode
      }
      
      return nextPart.monetization_amount || 0;
    }
    return 0;
  }

  calculateReadingTime(): number {
    if (!this.storyContent?.content) return 0;
    const wordCount = this.storyContent.content.split(/\s+/).length;
    return Math.ceil(wordCount / 200);
  }

  formatDate(dateInput: any): string {
    // console.log('Date Input:', dateInput); 
    
    if (!dateInput) return 'N/A';
    
    let date: Date;
    
    // Try different parsing methods
    if (typeof dateInput === 'string') {
        // Try ISO format first
        date = new Date(dateInput);
        
        // If invalid, try timestamp
        if (isNaN(date.getTime())) {
            const timestamp = parseInt(dateInput);
            if (!isNaN(timestamp)) {
                date = new Date(timestamp);
            } else {
                // Try removing timezone if present
                const cleanedDate = dateInput.split(' ')[0];
                date = new Date(cleanedDate);
            }
        }
    } else if (typeof dateInput === 'number') {
        date = new Date(dateInput);
    } else {
        return 'N/A';
    }
    
    // Final check
    if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateInput);
        return 'N/A';
    }
    
    // Format to readable string
    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    };
    
    return date.toLocaleDateString('en-IN', options);
  }

  getReadPercentage(): number {
    const storyEl = document.getElementById('storyContent');
    if (!storyEl) return 0;
    
    const scrollTop = storyEl.scrollTop || window.scrollY;
    const docHeight = storyEl.scrollHeight - storyEl.clientHeight;
    
    if (docHeight <= 0) return 0;
    return Math.round((scrollTop / docHeight) * 100);
  }

  goToPreviousPart(): void {
    const currentIndex = this.allParts.findIndex(part => part.id == this.storyId);
    if (currentIndex > 0) {
      const prevPart = this.allParts[currentIndex - 1];
      this.navigateToPart(prevPart);
    }
  }

  navigateToPart(part: any): void {
    this.storyId = part.id;
    this.isLoading = true;
    this.storyContent = null;
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.loadStory();
    
    const slug = this.slugify(part.story_title || 'story');
    this.router.navigate(['/series', `${part.id}-${slug}`], { replaceUrl: true });
  }

  goBackToStory(): void {
    if (this.storyDetail?.stories_id) {
      this.router.navigate(['/story', `${this.storyDetail.stories_id}-${this.slugify(this.storyDetail.story_title)}`]);
    }
  }

  goToWallet(): void {
    this.router.navigate(['/wallet']);
  }

  shareStory(): void {
    if (!this.storyContent) return;
    
    const storyUrl = window.location.href;
    const storyTitle = this.storyContent.title || 'Check out this story';
    
    navigator.clipboard.writeText(`${storyTitle}\n${storyUrl}`).then(() => {
      alert('Story link copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  }

  checkIfInLibrary(): void {
    if (!this.storyId || !this.userId) {
      this.isInLibrary = false;
      return;
    }
    
    this.isInLibrary = false;
  }

  addToLibrary(): void {
    if (!this.isUserLoggedIn) {
      this.navigateToLogin();
      return;
    }
    
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      this.navigateToLogin();
      return;
    }
    
    this.isInLibrary = !this.isInLibrary;
    
    console.log('Library status toggled:', this.isInLibrary);
  }

  // ========== SHARE METHODS ==========
  shareOnFacebook(event: Event): void {
    event.preventDefault();
    if (!this.storyContent || !this.storyDetail) {
      alert('Story data not loaded yet.');
      return;
    }

    const storyUrl = `https://stories.funtelstudio.com/storyapi/${this.storyId}-${this.slugify(this.storyContent.title)}`;
    const imageUrl = this.storyDetail.story_image 
      ? `https://stories.funtelstudio.com/storyapi/${this.storyDetail.user_id}/${this.storyDetail.story_image}`
      : 'https://stories.funtelstudio.com/storyapi/default-story-image.jpg';

    const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(storyUrl)}&picture=${encodeURIComponent(imageUrl)}&title=${encodeURIComponent(this.storyContent.title)}&description=${encodeURIComponent(this.storyContent.description)}`;
    
    window.open(fbShareUrl, '_blank', 'width=600,height=400');
  }

  shareOnTwitter(): void {
    if (!this.storyContent || !this.storyDetail) return;

    const storyUrl = `https://stories.funtelstudio.com/storyapi/${this.storyId}-${this.slugify(this.storyContent.title)}`;
    const imageUrl = this.storyDetail.story_image 
      ? `https://stories.funtelstudio.com/storyapi/${this.storyDetail.user_id}/${this.storyDetail.story_image}`
      : 'https://stories.funtelstudio.com/storyapi/default-story-image.jpg';

    const text = `Check out this story: ${this.storyContent.title}`;
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(storyUrl)}&text=${encodeURIComponent(text)}&hashtags=Stories`;
    
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  }

  shareOnWhatsApp(event: Event): void {
    event.preventDefault();
    if (!this.storyContent) return;

    const storyUrl = `https://stories.funtelstudio.com/storyapi/${this.storyId}-${this.slugify(this.storyContent.title)}`;
    const text = `Check out this story: ${this.storyContent.title} - ${storyUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    
    window.open(whatsappUrl, '_blank', 'width=600,height=400');
  }

  // ========== UTILITY METHODS ==========
  slugify(text: string): string {
    return text.toLowerCase().trim().replace(/[\s\W-]+/g, '-').replace(/^-+|-+$/g, '');
  }

  t(key: string): string {
    return this.translationService.translate(key);
  }

  formatCount(num: number): string {
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
    return num.toString();
  }

  storeReadProgress(storyId: number, partId: number, totalParts: number, partTitle: string): void {
    const userId = this.authService.getCurrentUserId();
    if (!userId) return;

    const key = `story_progress_${userId}`;
    const progressData = JSON.parse(localStorage.getItem(key) || '{}');

    if (!progressData[storyId]) {
      progressData[storyId] = {
        lastReadPartId: partId,
        readParts: [partId],
        totalParts: totalParts,
        lastPartTitle: partTitle,
        timestamp: Date.now()
      };
    } else {
      const entry = progressData[storyId];
      entry.lastReadPartId = partId;
      entry.lastPartTitle = partTitle;
      entry.timestamp = Date.now();
      if (!entry.readParts.includes(partId)) {
        entry.readParts.push(partId);
      }
    }

    localStorage.setItem(key, JSON.stringify(progressData));
  }

  onImageError(): void {
    this.hideAuthorImage = true;
  }

  getSafeVideoUrl(url: string): SafeResourceUrl | null {
    if (!url) return null;

    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${videoId}`);
    }

    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1];
      return this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${videoId}`);
    }

    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1];
      return this.sanitizer.bypassSecurityTrustResourceUrl(`https://player.vimeo.com/video/${videoId}`);
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  getFormattedRating(rating: any): string {
    if (rating === null || rating === undefined) {
      return '0.0';
    }
    
    const num = parseFloat(rating);
    if (isNaN(num)) {
      return '0.0';
    }
    
    return num.toFixed(1);
  }

  // ========== HOST LISTENERS ==========
  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrolled = (scrollTop / docHeight) * 100;
    this.scrollProgress = `${scrolled}%`;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isFullscreen) {
      const controls = document.getElementById('readerControls');
      if (controls) {
        controls.style.opacity = '1';
        
        clearTimeout(this.controlsTimeout);
        this.controlsTimeout = setTimeout(() => {
          if (this.isFullscreen) {
            controls.style.opacity = '0.3';
          }
        }, 3000);
      }
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isFullscreen) {
      this.toggleFullscreen();
    }
  }
}